import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import './InstallerPanelTable.css';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { DataView } from 'primereact/dataview';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { exportDataTableCsv } from '../../helpers/exportCsv';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import { FilterMatchMode, FilterService } from 'primereact/api';
import { connect } from 'react-redux';
import { ISystem } from '../../helpers/interfaces';
import { getSystemsVeryShort, getAuditChangesForSystem } from '../../stores/actions/installerActions';

interface AuditChangesProps {
    systems: ISystem[];
    mobileView: boolean;
    auditChanges: any[];
    auditChangesLoading: boolean;
    getSystemsVeryShort: () => void;
    getAuditChangesForSystem: (systemId: number) => void;
}

const tableLabels = {
    systems: 'System',
    nodes: 'Node',
    routers: 'Router',
    devices: 'Device',
};

const AuditChanges = ({
    systems,
    mobileView,
    auditChanges,
    auditChangesLoading,
    getSystemsVeryShort,
    getAuditChangesForSystem,
}: AuditChangesProps) => {
    const dt = useRef(null);
    const [systemNames, setSystemNames] = useState([]);
    const [selectedSystemName, setSelectedSystemName] = useState(null);

    useEffect(() => {
        getSystemsVeryShort();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (systems) {
            setSystemNames(systems.map(el => el.name).sort());
        }
    }, [systems]);

    const refresh = () => {
        if (!selectedSystemName) return;
        const systemIdsByNames = Object.fromEntries(systems.map(x => [x.name, x.ID]));
        const systemId = systemIdsByNames[selectedSystemName];
        if (systemId) {
            getAuditChangesForSystem(systemId);
        }
    };

    const onSystemChange = (e) => {
        setSelectedSystemName(e.value);
        const systemIdsByNames = Object.fromEntries(systems.map(x => [x.name, x.ID]));
        const systemId = systemIdsByNames[e.value];
        if (systemId) {
            getAuditChangesForSystem(systemId);
        }
    };

    // Enrich with display-ready strings once, up front, so DataTable's built-in field
    // filters (CONTAINS on a string) can work directly against them - including the
    // Record column, which doubles as the "filter by node/device" search since it
    // already carries the node/device's name.
    const items = useMemo(() => {
        if (!selectedSystemName) return [];
        return (auditChanges || []).map((c) => {
            const tableLabel = tableLabels[c.tableName] || c.tableName;
            return {
                ...c,
                // event_id alone isn't unique here: one audit event that changes several
                // tracked fields at once now produces one row per field, all sharing the
                // same eventId. Without a unique row key, React/PrimeReact can reuse a
                // filtered-out row's rendered cells for a different row after filtering.
                rowKey: `${c.eventId}-${c.field ?? 'insert'}`,
                tableLabel,
                recordDisplay: `${tableLabel}: ${c.recordLabel || '---'} (#${c.recordId})`,
                occurredAtDisplay: c.occurredAt ? (new Date(c.occurredAt)).toLocaleString('sv-SE') : '',
                changedByDisplay: c.changedByName || '---',
                fieldDisplay: c.action === 'I' ? 'New node' : (c.field || ''),
            };
        });
    }, [auditChanges, selectedSystemName]);

    // Day-level range filter on the raw timestamp, same approach as the Date column on
    // the Service Actions page - a plain "contains" text filter on a formatted
    // timestamp is fiddly to use, a from/to picker isn't.
    FilterService.register('occurred_at_date_range', (value, rangeFilter) => {
        if (!value) return false;

        const rowDate = new Date(value);

        const start = rangeFilter?.dateStart ? new Date(rangeFilter.dateStart) : null;
        const end = rangeFilter?.dateEnd ? new Date(rangeFilter.dateEnd) : null;
        if (end) end.setHours(23, 59, 59, 999);

        if (start && end) return rowDate >= start && rowDate <= end;
        if (start) return rowDate >= start;
        if (end) return rowDate <= end;
        return true;
    });

    const [filters, setFilters] = useState({
        occurredAt: { value: { dateStart: null, dateEnd: null }, matchMode: 'occurred_at_date_range' },
        tableLabel: { value: null, matchMode: FilterMatchMode.EQUALS },
        recordDisplay: { value: null, matchMode: FilterMatchMode.CONTAINS },
        fieldDisplay: { value: null, matchMode: FilterMatchMode.CONTAINS },
        oldValue: { value: null, matchMode: FilterMatchMode.CONTAINS },
        newValue: { value: null, matchMode: FilterMatchMode.CONTAINS },
        changedByDisplay: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    const handleDateChange = (field: 'dateStart' | 'dateEnd', date: Date | null) => {
        const updatedDate = date
            ? new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString()
            : null;

        const newFilters = { ...filters };
        newFilters.occurredAt.value = { ...newFilters.occurredAt.value, [field]: updatedDate };
        setFilters(newFilters);
    };

    const dateRangeFilter = (field: 'dateStart' | 'dateEnd', id?: string) => {
        const currentValue = filters.occurredAt.value?.[field];
        return (
            <Calendar
                id={id}
                value={currentValue ? new Date(currentValue) : null}
                onChange={(e) => handleDateChange(field, e.value as Date | null)}
                placeholder="yyyy-mm-dd"
                dateFormat="yy-mm-dd"
                mask="9999-99-99"
                showButtonBar
            />
        );
    };

    const occurredAtTemplate = (rowData) => rowData.occurredAtDisplay;

    const tableFilterOptions = Object.values(tableLabels).map((label) => ({ label, value: label }));

    const tableFilterTemplate = (options) => {
        return (
            <Dropdown
                value={options.value}
                options={tableFilterOptions}
                onChange={(e) => options.filterApplyCallback(e.value)}
                placeholder="Any"
                showClear
            />
        );
    };

    const fieldTemplate = (rowData) => {
        if (rowData.action === 'I') {
            return <Tag value="New node" severity="success"></Tag>;
        }
        const reactivated = rowData.field === 'w2_operational' && rowData.newValue === 'true';
        return (
            <span className="flex align-items-center gap-2">
                {rowData.field}
                {reactivated && <Tag value="Reactivated" severity="success"></Tag>}
            </span>
        );
    };

    const cardTemplate = (rowData) => {
        return (
            <div className="col-12">
                <div className="border-1 surface-border border-round p-3 mb-2 surface-card">
                    <div className="flex justify-content-between align-items-center pb-2 mb-2 border-bottom-1 surface-border">
                        <span className="text-xs text-color-secondary">{rowData.occurredAtDisplay}</span>
                    </div>
                    <div className="mb-2">
                        <div className="text-xs text-color-secondary">Record</div>
                        <div className="font-medium" style={{ wordBreak: 'break-word' }}>{rowData.recordDisplay}</div>
                    </div>
                    <div className="mb-2">
                        <div className="text-xs text-color-secondary">Field</div>
                        <div className="font-medium" style={{ wordBreak: 'break-word' }}>{fieldTemplate(rowData)}</div>
                    </div>
                    {rowData.action !== 'I' && (
                        <div className="mb-2">
                            <div className="grid">
                                <div className="col-6">
                                    <div className="text-xs text-color-secondary">Old value</div>
                                    <div className="font-medium" style={{ wordBreak: 'break-word' }}>{rowData.oldValue ?? '---'}</div>
                                </div>
                                <div className="col-6">
                                    <div className="text-xs text-color-secondary">New value</div>
                                    <div className="font-medium" style={{ wordBreak: 'break-word' }}>{rowData.newValue ?? '---'}</div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div>
                        <div className="text-xs text-color-secondary">Changed by</div>
                        <div className="font-medium">{rowData.changedByDisplay}</div>
                    </div>
                </div>
            </div>
        );
    };

    const headerBar = (
        <div className="flex align-items-center gap-3 flex-wrap mb-4">
            <Dropdown
                value={selectedSystemName}
                onChange={onSystemChange}
                options={systemNames}
                filter
                placeholder="Select system"
                className={mobileView ? 'w-full' : 'w-20rem'}
            />
            <Button
                icon="pi pi-refresh"
                label="Refresh"
                outlined
                disabled={!selectedSystemName}
                loading={auditChangesLoading}
                onClick={refresh}
            />
            {!mobileView && (
                <Button
                    icon="pi pi-file-export"
                    label="Export"
                    outlined
                    disabled={!items?.length}
                    onClick={() => exportDataTableCsv(dt, 'audit-changes')}
                />
            )}
            <span className="text-color-secondary text-sm">Showing flagged changes from the last 7 days</span>
        </div>
    );

    const emptyMessage = selectedSystemName ? 'No flagged changes in the last 7 days.' : 'Pick a system to see its recent changes.';

    const dataView = useMemo(() => {
        return (
            <DataView
                value={items}
                dataKey="rowKey"
                itemTemplate={cardTemplate}
                paginator
                rows={10}
                emptyMessage={emptyMessage}
                loading={auditChangesLoading}
            />
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, auditChangesLoading, selectedSystemName]);

    const dataTable = useMemo(() => {
        return (
            <DataTable ref={dt} value={items} sortField="occurredAt" sortOrder={-1} exportFilename="audit-changes"
                    dataKey="rowKey" paginator rows={25} rowsPerPageOptions={[10, 25, 50, 100]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
                    stripedRows size="small" resizableColumns loading={auditChangesLoading}
                    filters={filters} filterDisplay="row"
                    emptyMessage={emptyMessage}>
                <Column
                    field="occurredAt"
                    header="Time"
                    body={occurredAtTemplate}
                    sortable
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={() => (
                        <div className="flex flex-col gap-1">
                            {dateRangeFilter('dateStart')}-{dateRangeFilter('dateEnd')}
                        </div>
                    )}
                    style={{ minWidth: '220px' }}>
                </Column>
                <Column field="tableLabel" header="Table" sortable filter showFilterMenu={false} filterElement={tableFilterTemplate} style={{ minWidth: '120px' }}></Column>
                <Column field="recordDisplay" header="Record" sortable filter filterPlaceholder="" showFilterMenu={false} style={{ minWidth: '220px' }}></Column>
                <Column field="fieldDisplay" header="Field" body={fieldTemplate} sortable filter filterPlaceholder="" showFilterMenu={false} style={{ minWidth: '180px' }}></Column>
                <Column field="oldValue" header="Old value" sortable filter filterPlaceholder="" showFilterMenu={false} style={{ minWidth: '140px' }}></Column>
                <Column field="newValue" header="New value" sortable filter filterPlaceholder="" showFilterMenu={false} style={{ minWidth: '140px' }}></Column>
                <Column field="changedByDisplay" header="Changed by" sortable filter filterPlaceholder="" showFilterMenu={false} style={{ minWidth: '160px' }}></Column>
            </DataTable>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, auditChangesLoading, filters]);

    return (
        <div>
            <div className="card">
                {headerBar}
                {mobileView ? dataView : dataTable}
            </div>
        </div>
    );
}

const mapStateToProps = (state) => {
    return {
        systems: state.dashboard.systemsVeryShort,
        mobileView: state.dashboard.mobileView,
        auditChanges: state.dashboard.auditChanges,
        auditChangesLoading: state.dashboard.auditChangesLoading,
    };
};

const actions = {
    getSystemsVeryShort,
    getAuditChangesForSystem,
};

export default connect(mapStateToProps, actions)(AuditChanges);
